import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { shipmentsApi } from '../../../../../../../api/shipment/shipmentsApi';
import { IInvoicePreview } from '../../../../../../../api/shipment/interfaces/IInvoicePreview';
import { INVOICE_PREVIEW_INITIAL_STATE } from '../initialState';

export const fetchInvoicePreviewDetails = createAsyncThunk(
    'fetchInvoicePreviewDetails',
    async (
        {
            shipmentId,
        }: {
            shipmentId: number;
        },
        { rejectWithValue },
    ) => {
        try {
            const response =
                await shipmentsApi.fetchShimpentInvoicePreviewDetails(
                    shipmentId,
                );
            return response;
        } catch {
            return rejectWithValue('Error');
        }
    },
);

export const fetchMultipleShipmentsInvoicePreviewDetails = createAsyncThunk(
    'fetchMultipleShipmentsInvoicePreviewDetails',
    async (
        {
            shipmentIds,
            customerId,
        }: {
            shipmentIds: number[];
            customerId?: number;
        },
        { rejectWithValue },
    ) => {
        try {
            const response =
                await shipmentsApi.fetchMultipleShipmentsInvoicePreviewDetails(
                    shipmentIds,
                    customerId,
                );
            return response;
        } catch {
            return rejectWithValue('Error');
        }
    },
);

export type IInvoicePreviewInitialState = {
    loading: boolean;
    invoicePreviewDetails: IInvoicePreview;
    multipleInvoicePreviewsLoading: boolean;
    multipleInvoicePreviews: IInvoicePreview[];
};

const initialState: IInvoicePreviewInitialState = {
    loading: false,
    invoicePreviewDetails: INVOICE_PREVIEW_INITIAL_STATE,
    multipleInvoicePreviewsLoading: false,
    multipleInvoicePreviews: [],
};

export const invoicePreviewSlice = createSlice({
    name: 'fetchInvoicePreviewDetails',
    initialState: initialState,
    reducers: {
        setInvoicePreviewDetails: (
            state,
            action: PayloadAction<IInvoicePreview>,
        ) => {
            state.invoicePreviewDetails = action.payload;
        },
        setMultipleInvoicePreviews: (
            state,
            action: PayloadAction<IInvoicePreview[]>,
        ) => {
            state.multipleInvoicePreviews = action.payload;
        },
        clearMultipleInvoicePreviews: state => {
            state.multipleInvoicePreviews = [];
            state.multipleInvoicePreviewsLoading = false;
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchInvoicePreviewDetails.pending, state => {
            state.loading = true;
            state.invoicePreviewDetails = INVOICE_PREVIEW_INITIAL_STATE;
        });
        builder.addCase(
            fetchInvoicePreviewDetails.fulfilled,
            (state, action) => {
                state.loading = false;
                state.invoicePreviewDetails = action.payload;
            },
        );
        builder.addCase(fetchInvoicePreviewDetails.rejected, state => {
            state.loading = false;
            state.invoicePreviewDetails = INVOICE_PREVIEW_INITIAL_STATE;
        });

        builder.addCase(
            fetchMultipleShipmentsInvoicePreviewDetails.pending,
            state => {
                state.multipleInvoicePreviewsLoading = true;
                state.multipleInvoicePreviews = [];
            },
        );
        builder.addCase(
            fetchMultipleShipmentsInvoicePreviewDetails.fulfilled,
            (state, action) => {
                state.multipleInvoicePreviewsLoading = false;
                state.multipleInvoicePreviews = action.payload;
            },
        );
        builder.addCase(
            fetchMultipleShipmentsInvoicePreviewDetails.rejected,
            state => {
                state.multipleInvoicePreviewsLoading = false;
                state.multipleInvoicePreviews = [];
            },
        );
    },
});

export const {
    setInvoicePreviewDetails,
    setMultipleInvoicePreviews,
    clearMultipleInvoicePreviews,
} = invoicePreviewSlice.actions;

const InvoicePreviewReducer = invoicePreviewSlice.reducer;
export default InvoicePreviewReducer;
